'use client';

import { Button } from '@ophelos/ui/components/button';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import type { DebtData } from '@/types';
import { accountCurrency, debtNameByTransaction } from '@/lib/accountPayment';
import { fromMillicents } from '@/lib/currency';
import { groupByPayment, paymentGroupDebtLabel } from '../_paymentHistoryPartials/paymentGroups';
import { formatLedgerDate } from '../_shared/charges';
import { customerTransactions, transactionLowersBalance, transactionShowsPlusSign, transactionTitle } from '../_shared/TransactionsList';

/** Enough to answer "did my payment land?" without turning the dashboard into a feed. */
const PREVIEW_COUNT = 3;

interface RecentPaymentsProps {
  debts: DebtData[];
}

export function RecentPayments({ debts }: RecentPaymentsProps) {
  const { t, currentLanguage } = useTranslation();

  const payments = groupByPayment(customerTransactions(debts)).slice(0, PREVIEW_COUNT);
  if (payments.length === 0) return null;

  const debtNames = debtNameByTransaction(debts);
  const currency = accountCurrency(debts);

  // Same shape as a DebtGroup: title row with the action where a group shows its badge, then
  // one card per row.
  return (
    <div className="gap-2 overflow-visible mt-5">
      <div className="h-full px-2 flex items-center justify-between mb-2">
        <div className="font-medium">{t('dashboard.recentPayments')}</div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/payment-history">
            {t('transaction.viewAll')}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>

      <div className="flex flex-col gap-8 bg-card rounded-2xl border p-4">
        {payments.map((payment) => {
          const lead = payment.splits[0];
          const lowersBalance = transactionLowersBalance(lead);
          const showPlus = transactionShowsPlusSign(lead);
          const DirectionIcon = lowersBalance ? ArrowDownRight : ArrowUpRight;
          const [titleKey, titleValues] = transactionTitle(lead);

          return (
            <div key={payment.key} className="flex items-start justify-between gap-3">
              <div className="flex gap-3 flex-1 min-w-0">
                <div className={`size-8 rounded-full flex items-center justify-center shrink-0 ${lowersBalance ? 'highlight-green' : 'bg-soft'}`}>
                  <DirectionIcon className="size-4" />
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <h5 className="font-medium text-sm text-left">{t(titleKey, titleValues)}</h5>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                    <span className="shrink-0">{formatLedgerDate(payment.createdAt, currentLanguage)}</span>
                    <span>
                      {t('transaction.appliedTo')} <span className="font-medium">{paymentGroupDebtLabel(payment, debtNames, t)}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div
                className={`inline-flex gap-1 items-center shrink-0 self-center whitespace-nowrap tabular-nums ${lowersBalance ? 'text-green-600' : showPlus ? 'text-amber-600' : ''}`}
              >
                <span className="text-base">{lowersBalance ? '-' : showPlus ? '+' : ''}</span>
                <CurrencyDisplay amount={fromMillicents(payment.amountMillicents)} currency={currency} className="font-semibold text-base" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
